export type DateInput = string | number | Date | null | undefined;

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
};

const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
};

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

const toDate = (value: DateInput): Date | null => {
    if (!value) return null;
    if (value instanceof Date) {
        return isValidDate(value) ? value : null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
        const date = new Date(normalized);
        return isValidDate(date) ? date : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
            return null;
        }
        const date = new Date(trimmed);
        return isValidDate(date) ? date : null;
    }
    return null;
};

export const formatDate = (
    value: DateInput,
    options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
    fallback = 'N/A',
    locale = 'en-US'
): string => {
    const date = toDate(value);
    if (!date) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        return fallback;
    }
    return date.toLocaleDateString(locale, options);
};

export const formatDateTime = (
    value: DateInput,
    options: Intl.DateTimeFormatOptions = DEFAULT_DATE_TIME_OPTIONS,
    fallback = 'N/A',
    locale = 'en-US'
): string => {
    const date = toDate(value);
    if (!date) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        return fallback;
    }
    return date.toLocaleString(locale, options);
};

export const formatDateLabel = (value: DateInput, fallback = ''): string =>
    formatDate(value, { month: 'short', day: 'numeric' }, fallback);

export const formatDateWithWeekday = (value: DateInput, fallback = 'N/A'): string =>
    formatDate(value, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }, fallback);

const getOrdinalSuffix = (day: number): string => {
    if (day % 100 >= 11 && day % 100 <= 13) {
        return 'th';
    }
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const formatDateWithOrdinal = (
    value: DateInput,
    options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' },
    fallback = 'N/A',
    locale = 'en-US'
): string => {
    const date = toDate(value);
    if (!date) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        return fallback;
    }
    const day = date.getDate();
    const formatted = date.toLocaleDateString(locale, options);
    return formatted.replace(/\b\d{1,2}\b/, `${day}${getOrdinalSuffix(day)}`);
};

const parseTimeString = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!match) return null;

    if (!match[1]) return null;
    let hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2] ?? '0', 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null;
    }

    const meridiem = match[4]?.toLowerCase();
    if (meridiem) {
        if (meridiem === 'pm' && hours < 12) {
            hours += 12;
        }
        if (meridiem === 'am' && hours === 12) {
            hours = 0;
        }
    }

    return {
        hours,
        minutes,
    };
};

export const formatTime = (
    value?: string | number | Date | null,
    fallback = '',
    format: '12h' | '24h' = '12h'
): string => {
    if (!value) return fallback;
    if (value instanceof Date) {
        if (!isValidDate(value)) return fallback;
        return value.toLocaleTimeString('en-US', {
            hour: format === '24h' ? '2-digit' : 'numeric',
            minute: '2-digit',
            hour12: format === '12h',
        });
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        const date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
        return isValidDate(date)
            ? date.toLocaleTimeString('en-US', {
                hour: format === '24h' ? '2-digit' : 'numeric',
                minute: '2-digit',
                hour12: format === '12h',
            })
            : fallback;
    }
    if (typeof value !== 'string') {
        return fallback;
    }

    const parsed = parseTimeString(value);
    if (!parsed) {
        return value;
    }
    if (format === '24h') {
        const hours = parsed.hours.toString().padStart(2, '0');
        const minutes = parsed.minutes.toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    const period = parsed.hours >= 12 ? 'PM' : 'AM';
    const displayHours = parsed.hours % 12 || 12;
    const minutes = parsed.minutes.toString().padStart(2, '0');
    return `${displayHours}:${minutes} ${period}`;
};

export const formatTimeRange = (
    start?: string | null,
    end?: string | null,
    fallback = 'Time TBD'
): string => {
    const startLabel = start ? formatTime(start) : '';
    const endLabel = end ? formatTime(end) : '';

    if (!startLabel && !endLabel) {
        return fallback;
    }
    if (startLabel && endLabel) {
        return `${startLabel} - ${endLabel}`;
    }
    return startLabel || endLabel;
};
